import { BoundingBox, BoundingBoxObject } from './BoundingBox';
import { Octree } from './Octree';

import { vec3 } from 'gl-matrix';
import { Ray } from '../math';

export class SpatialDatabase<T extends BoundingBoxObject> {
  public octree: Octree<T>;
  private readonly boundingRegion: BoundingBox;

  constructor(boundingRegion: BoundingBox) {
    this.boundingRegion = boundingRegion;
    this.octree = Octree.create<T>(boundingRegion);
  }

  public clear() {
    this.octree = Octree.create<T>(this.boundingRegion);
  }

  public handleMovementArray(objArray: T[]) {
    for (let obj of objArray) {
      this.handleMovement(obj);
    }
  }

  public handleMovement(obj: T) {
    this.remove(obj);
    this.add(obj);
  }

  public removeArray(objArray: T[]) {
    for (let obj of objArray) {
      this.remove(obj);
    }
  }

  public remove(obj: T) {
    this.octree.removeEverywhere(obj);
  }

  public add(obj: T) {
    this.octree.insert(obj, false);
  }

  public addArray(objArray: T[]) {
    this.octree.insertArray(objArray, false);
  }

  public optimize() {
    this.octree.prune();
  }

  public rayTrace(ray: Ray) {
    return this.octree.rayTrace(ray);
  }

  public getObjectsInRange(position: vec3, range: number): T[] {
    let bounds = new BoundingBox(
      vec3.fromValues(position[0] - range, position[1] - range, position[2] - range),
      vec3.fromValues(position[0] + range, position[1] + range, position[2] + range),
    );
    let objects = this.octree.getObjectsInRegion(bounds);

    return objects.filter((obj) => vec3.distance(position, obj.getBoundingBox().center) <= range);
  }

  public getObjectsInRegion(bounds: BoundingBox) {
    return this.octree.getObjectsInRegion(bounds);
  }
}
